import type { ReactNode } from 'react';
import { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getConfig } from '../config';
import { Stage } from '../config/ApplicationConfig';

interface User {
  email: string;
  name: string;
  picture: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (userData: User) => void;
  logout: () => void;
  validateDomain: (email: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const ALLOWED_DOMAIN = 'skin-seoul.com';

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const navigate = useNavigate();

  const validateDomain = (email: string): boolean => {
    if (getConfig().stage === Stage.LOCAL) {
      return true;
    }
    return email.endsWith(`@${ALLOWED_DOMAIN}`);
  };

  useEffect(() => {
    // Initialize user from localStorage on component mount
    const storedUser = localStorage.getItem('user');

    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);

        // Validate that the stored user has the correct domain
        if (!validateDomain(parsedUser.email)) {
          console.error('Invalid email domain in stored user. Logging out:', parsedUser.email);
          localStorage.removeItem('user');
          return;
        }

        console.log('Loaded user from localStorage:', parsedUser.email);
        setUser(parsedUser);
      } catch (error) {
        console.error('Error parsing user from localStorage', error);
        localStorage.removeItem('user');
      }
    }
  }, []);

  const login = (userData: User) => {
    // Double-check domain before login
    if (!validateDomain(userData.email)) {
      console.error('Attempted login with unauthorized email domain:', userData.email);
      return;
    }

    console.log('Setting user in AuthProvider:', userData.email);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const logout = () => {
    console.log('Logging out user');
    localStorage.removeItem('user');
    setUser(null);
    navigate('/login');
  };

  const authContextValue = {
    user,
    isAuthenticated: Boolean(user),
    login,
    logout,
    validateDomain,
  };

  return <AuthContext.Provider value={authContextValue}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
